(function () {
	/*
	 * QA-GLOBAL (2026-09, Dev QA sheet) — two modes.
	 *
	 * Desktop (>= 1100px, or any width when the merchant turns "Autoplay on
	 * mobile" on): the original continuous marquee. Swiper 11 loop mode needs
	 * more slides than are visible; with 4 cards at 4.4 per view it silently
	 * degraded and the row opened on the SECOND card. The slides are now
	 * cloned (aria-hidden, out of the tab order) until loop mode has enough to work with,
	 * so the row opens on card 1.
	 *
	 * Touch (< 1100px, default): a shopper-driven row — no autoplay (the
	 * marquee's 20s `speed` also slowed every swipe to a crawl), a partial
	 * peek of the next card, an edge fade and prev/next arrows, matching the
	 * PLP category-tile strip (<bj-tile-scroller>). Starts on card 1, and is
	 * reset to card 1 on a back/forward-cache restore.
	 */
	const TOUCH_QUERY = window.matchMedia("(max-width: 1099px)");
	const CLONE_CLASS = "bj-mc-clone";

	function toBoolean(string) {
		return string === "true" ? true : false;
	}

	const isTouchMode = (slider) =>
		TOUCH_QUERY.matches && !toBoolean(slider.dataset.mobileAutoplay);

	const removeClones = (wrapper) => {
		wrapper.querySelectorAll(`.${CLONE_CLASS}`).forEach((el) => el.remove());
	};

	// Loop mode needs roughly two views' worth of slides. Clone the real set
	// until there are, and hide the copies from assistive tech / tab order.
	const ensureLoopSlides = (wrapper, perView) => {
		const originals = Array.from(wrapper.children).filter(
			(el) => el.classList.contains("swiper-slide") && !el.classList.contains(CLONE_CLASS)
		);
		if (originals.length === 0) return;
		const needed = Math.ceil(perView) * 2 + 2;
		let count = originals.length;
		while (count < needed) {
			originals.forEach((el) => {
				const clone = el.cloneNode(true);
				clone.classList.add(CLONE_CLASS);
				clone.setAttribute("aria-hidden", "true");
				// Not inert: the copies are visible marquee cards and must stay
				// clickable. Hidden from assistive tech and the tab order only.
				clone.querySelectorAll("a, button, [tabindex]").forEach((el) => el.setAttribute("tabindex", "-1"));
				clone.removeAttribute("data-shopify-editor-block");
				wrapper.appendChild(clone);
			});
			count += originals.length;
		}
	};

	const updateArrows = (slider, swiper) => {
		const scrollable = !swiper.isLocked;
		slider.toggleAttribute("data-can-prev", scrollable && !swiper.isBeginning);
		slider.toggleAttribute("data-can-next", scrollable && !swiper.isEnd);
	};

	const bindArrows = (slider) => {
		if (slider.dataset.bjArrowsBound) return;
		slider.dataset.bjArrowsBound = "true";
		slider.querySelector("[data-bj-mc-prev]")?.addEventListener("click", () => {
			slider.bjSwiper?.slidePrev();
		});
		slider.querySelector("[data-bj-mc-next]")?.addEventListener("click", () => {
			slider.bjSwiper?.slideNext();
		});
	};

	const initTouch = (slider, swiperEl) => {
		const mobileR = +slider.dataset.mobile || 1;
		slider.classList.add("multicolumn-slider--touch");
		bindArrows(slider);
		const swiper = new Swiper(swiperEl, {
			loop: false,
			initialSlide: 0,
			slidesPerView: mobileR + 0.2,
			speed: 450,
			longSwipesRatio: 0.1,
			allowTouchMove: true,
			watchOverflow: true,
			spaceBetween: 16,
			breakpoints: {
				750: {
					slidesPerView: Math.max(mobileR, 2) + 0.2,
				},
			},
			on: {
				afterInit: (s) => updateArrows(slider, s),
				slideChange: (s) => updateArrows(slider, s),
				reachBeginning: (s) => updateArrows(slider, s),
				reachEnd: (s) => updateArrows(slider, s),
				fromEdge: (s) => updateArrows(slider, s),
				resize: (s) => updateArrows(slider, s),
				lock: (s) => updateArrows(slider, s),
				unlock: (s) => updateArrows(slider, s),
			},
		});
		updateArrows(slider, swiper);
		return swiper;
	};

	const initMarquee = (slider, swiperEl) => {
		const sectionId = slider.dataset.id;
		const perRow = slider.dataset.perRow;
		const mobileR = slider.dataset.mobile;
		const speed = slider.dataset.speed * 1000;
		const autoplay = toBoolean(slider.dataset.autoplay);
		const enablenNavigation = toBoolean(slider.dataset.navigation);
		const stopAutoplaySlider = toBoolean(slider.dataset.stopAutoplay);
		let autoplayParm = {};

		slider.classList.remove("multicolumn-slider--touch");
		slider.removeAttribute("data-can-prev");
		slider.removeAttribute("data-can-next");

		ensureLoopSlides(
			swiperEl.querySelector(".swiper-wrapper"),
			Math.max(+perRow + 0.4, +mobileR)
		);

		if (autoplay) {
			autoplayParm = {
				autoplay: {
					delay: 0,
					disableOnInteraction: false,
				},
			};
		}

		const multicolumnSwiper = new Swiper(swiperEl, {
			loop: true,
			initialSlide: 0,
			longSwipesRatio: 0.1,
			slidesPerView: mobileR,
			allowTouchMove: true,
			speed: speed,
			breakpoints: {
				//576: {
				//  slidesPerView: 2,
				//},
				1100: {
					slidesPerView: +perRow + 0.4,
				},
			},
			...autoplayParm,
		});

		// The hover-pause / cursor navigation below is bound once per slider
		// and always talks to the CURRENT instance (slider.bjSwiper), because
		// crossing the touch breakpoint rebuilds Swiper.
		if (autoplay && stopAutoplaySlider && !slider.dataset.bjMarqueeBound) {
			slider.dataset.bjMarqueeBound = "true";
			let duration;
			let distanceRatio;
			let startTimer;
			const current = () => slider.bjSwiper;
			const active = () => current() && !isTouchMode(slider);

			const stopAutoplay = () => {
				const swiper = current();
				if (startTimer) clearTimeout(startTimer);

				swiper.setTranslate(swiper.getTranslate());

				const currentSlideWidth = swiper.slides[swiper.activeIndex].offsetWidth;
				distanceRatio = Math.abs(
					(currentSlideWidth * swiper.activeIndex + swiper.getTranslate()) /
						currentSlideWidth
				);

				duration = swiper.params.speed * distanceRatio;
				swiper.autoplay.stop();
			};

			const startAutoplay = (delay = duration) => {
				startTimer = setTimeout(() => {
					if (active()) current().autoplay.start();
				}, delay);
			};

			slider.addEventListener("mouseenter", function () {
				if (!active()) return;
				stopAutoplay();
			});

			slider.addEventListener("mouseleave", function () {
				if (!active()) return;
				const swiper = current();
				const distance = swiper.width * swiper.activeIndex + swiper.getTranslate();

				duration = distance !== 0 ? duration : 0;
				swiper.slideTo(swiper.activeIndex, duration);
				startAutoplay();
			});

			if (enablenNavigation) {
				// Only now do the edge hit-areas do anything; CSS keeps them
				// click-through otherwise (component-bj-multicolumn.css).
				slider.classList.add("multicolumn-slider--nav-bound");
				const handleSwitch = (setIndex) => {
					if (!active()) return;
					const swiper = current();
					stopAutoplay();
					const switchSpeed = 1000;
					const index = setIndex(swiper);
					if (index === 999) {
						swiper.slideNext(switchSpeed);
					} else if (index === -999) {
						swiper.slidePrev(switchSpeed);
					} else {
						swiper.slideTo(index, switchSpeed);
					}
				};

				document
					.querySelector(`#${sectionId} .multicolumn-button-next`)
					?.addEventListener(
						"click",
						function () {
							handleSwitch(function (swiper) {
								let nextIndex = swiper.activeIndex;
								const lastSwiperIndex = swiper.slides.length - 1;

								nextIndex =
									nextIndex > lastSwiperIndex ? lastSwiperIndex - nextIndex : nextIndex;

								return distanceRatio < 0.003 ? 999 : nextIndex;
							});
						},
						false
					);

				document
					.querySelector(`#${sectionId} .multicolumn-button-prev`)
					?.addEventListener(
						"click",
						function () {
							handleSwitch(function (swiper) {
								let prevIndex = swiper.activeIndex - 1;
								prevIndex = prevIndex < 0 ? swiper.slides.length + prevIndex : prevIndex;

								return distanceRatio === 0 ? -999 : prevIndex;
							});
						},
						false
					);

				const cursor = document.getElementById(`${sectionId}-cursor`);

				const animateCursor = (e) => {
					const x = e.clientX - cursor.offsetWidth / 2,
						y = e.clientY - cursor.offsetHeight / 2;

					const keyframes = {
						transform: `translate(${x}px, ${y}px) `,
					};

					cursor.animate(keyframes, {
						duration: 100,
						fill: "forwards",
					});
				};

				const getCursorClass = (type) => {
					switch (type) {
						case "next":
							return "cursor-icon next button button--simple";
						case "prev":
							return "cursor-icon prev button button--simple";
						default:
							return "cursor-icon button button--simple";
					}
				};

				const cursorArea = document.querySelector(`#${sectionId} .multicolumn-cursor`);
				if (cursor && cursorArea) {
					cursorArea.onmousemove = (e) => {
						const interactable = e.target.closest(".multicolumn__buttons"),
							interacting = interactable !== null;

						const icon = document.getElementById(`${sectionId}-cursor-icon`);

						if (e.target.closest(".multicolumn__buttons") != null) {
							animateCursor(e);
						}

						cursor.dataset.type = interacting ? interactable.dataset.type : "";

						if (interacting) {
							icon.className = getCursorClass(interactable.dataset.type);
						}
					};
				}
			}
		}

		return multicolumnSwiper;
	};

	const initSlider = (slider) => {
		const swiperEl = slider.querySelector(".swiper");
		if (!swiperEl) return;
		if (slider.bjSwiper) {
			slider.bjSwiper.destroy(true, true);
			slider.bjSwiper = null;
		}
		removeClones(swiperEl.querySelector(".swiper-wrapper"));
		slider.bjSwiper = isTouchMode(slider)
			? initTouch(slider, swiperEl)
			: initMarquee(slider, swiperEl);
	};

	const multicolumnSlider = () => {
		document.querySelectorAll(".multicolumn-slider").forEach(initSlider);
	};

	// Crossing the touch breakpoint swaps modes.
	const onModeChange = () => multicolumnSlider();
	if (TOUCH_QUERY.addEventListener) {
		TOUCH_QUERY.addEventListener("change", onModeChange);
	} else if (TOUCH_QUERY.addListener) {
		TOUCH_QUERY.addListener(onModeChange);
	}

	// Back/forward cache restores the page mid-scroll; start again on card 1.
	window.addEventListener("pageshow", (event) => {
		if (!event.persisted) return;
		document.querySelectorAll(".multicolumn-slider").forEach((slider) => {
			const swiper = slider.bjSwiper;
			if (!swiper || swiper.destroyed) return;
			if (swiper.params.loop) {
				swiper.slideToLoop(0, 0);
				if (swiper.params.autoplay && swiper.params.autoplay.enabled) swiper.autoplay.start();
			} else {
				swiper.slideTo(0, 0);
			}
		});
	});

	function detectDeviceType() {
		if (isiPad()) {
			const multicolumnLists = document.querySelectorAll(".multicolumn-list");
			multicolumnLists.forEach(elem => {
				if (elem) {
					elem.classList.add("multicolumn-list__ipad");
				}
			});
		}
	}

	document.addEventListener("DOMContentLoaded", function () {
		multicolumnSlider();
		detectDeviceType();
		document.addEventListener("shopify:section:load", function () {
			multicolumnSlider();
			detectDeviceType();
		});
	});
})();
